/**
 * Session 7 Phase 1 — Tenant detail + suspend/reactivate/archive.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../../components/ui/tabs";

import { GdprPanel, MetadataSnapshotModal } from "./OperatorGdpr";

function StatusBadge({ status }) {
  const map = {
    active: "secondary", pending_approval: "default",
    suspended: "destructive", archived: "outline",
    deletion_pending: "destructive", hard_deleted: "outline",
  };
  return <Badge variant={map[status] || "secondary"}>{status}</Badge>;
}

export default function OperatorTenantDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [reason, setReason] = useState("");
  const [confirmSlug, setConfirmSlug] = useState("");
  const [busy, setBusy] = useState(false);
  const [showMetadata, setShowMetadata] = useState(false);

  const load = () => api.get(`/operator/tenants/${id}`).then((r) => setT(r.data))
    .catch(() => toast.error("Tenant nicht gefunden."));
  useEffect(() => { load(); }, [id]);   // eslint-disable-line

  const mutate = async (action, body) => {
    setBusy(true);
    try {
      await api.post(`/operator/tenants/${id}/${action}`, body);
      toast.success(`Aktion ausgeführt: ${action}`);
      setReason(""); setConfirmSlug("");
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.error || d.hint)) || "Aktion fehlgeschlagen");
    } finally { setBusy(false); }
  };

  if (!t) return <div data-testid="tenant-detail-loading">Lade …</div>;

  return (
    <div className="space-y-6" data-testid="operator-tenant-detail">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-semibold tracking-tight">
              {t.name}
            </h1>
            <StatusBadge status={t.status} />
            <Badge variant="outline">{t.plan || "free"}</Badge>
          </div>
          <code className="text-xs font-mono text-muted-foreground">{t.slug}</code>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm"
                   onClick={() => setShowMetadata(true)}
                   data-testid="tenant-action-metadata-snapshot">
            Metadaten-Ansicht
          </Button>
          <Link to="/operator/tenants">
            <Button variant="outline" size="sm">← Zurück</Button>
          </Link>
        </div>
      </header>

      {/* Action row */}
      <div className="flex flex-wrap gap-2" data-testid="tenant-actions">
        {t.status === "active" && (
          <ActionDialog
            testIdRoot="suspend"
            trigger={<Button size="sm" variant="destructive"
                              data-testid="tenant-action-suspend">Suspendieren</Button>}
            title={`Tenant "${t.slug}" suspendieren?`}
            body={
              <Textarea
                rows={3}
                placeholder="Grund (min. 3 Zeichen)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="tenant-suspend-reason"
              />
            }
            onConfirm={() => mutate("suspend", { reason })}
            confirmDisabled={busy || reason.trim().length < 3}
            confirmLabel="Suspendieren"
          />
        )}
        {t.status === "suspended" && (
          <ActionDialog
            testIdRoot="reactivate"
            trigger={<Button size="sm"
                              data-testid="tenant-action-reactivate">Reaktivieren</Button>}
            title={`Tenant "${t.slug}" reaktivieren?`}
            body={
              <Textarea
                rows={3}
                placeholder="Grund (min. 3 Zeichen)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="tenant-reactivate-reason"
              />
            }
            onConfirm={() => mutate("reactivate", { reason })}
            confirmDisabled={busy || reason.trim().length < 3}
            confirmLabel="Reaktivieren"
          />
        )}
        {t.status !== "archived" && (
          <ActionDialog
            testIdRoot="archive"
            trigger={<Button size="sm" variant="outline"
                              data-testid="tenant-action-archive">Archivieren</Button>}
            title={`Tenant "${t.slug}" archivieren?`}
            body={
              <div className="space-y-3">
                <Textarea
                  rows={3}
                  placeholder="Grund (min. 3 Zeichen)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  data-testid="tenant-archive-reason"
                />
                <div>
                  <div className="text-xs text-muted-foreground mb-1">
                    Tippe den Slug <code>{t.slug}</code> zur Bestätigung ein:
                  </div>
                  <Input
                    value={confirmSlug}
                    onChange={(e) => setConfirmSlug(e.target.value)}
                    placeholder={t.slug}
                    data-testid="tenant-archive-confirm-slug"
                  />
                </div>
              </div>
            }
            onConfirm={() => mutate("archive",
              { reason, confirm_slug: confirmSlug })}
            confirmDisabled={busy || reason.trim().length < 3
                                    || confirmSlug !== t.slug}
            confirmLabel="Archivieren"
          />
        )}
      </div>

      <Tabs defaultValue="overview">
        <TabsList data-testid="tenant-tabs">
          <TabsTrigger value="overview" data-testid="tenant-tab-overview">Übersicht</TabsTrigger>
          <TabsTrigger value="metrics"  data-testid="tenant-tab-metrics">Kennzahlen</TabsTrigger>
          <TabsTrigger value="audit"    data-testid="tenant-tab-audit">Audit</TabsTrigger>
          <TabsTrigger value="gdpr"     data-testid="tenant-tab-gdpr">DSGVO</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Metadaten</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 gap-2 text-sm">
              <Meta k="Industry" v={t.industry} />
              <Meta k="Locale"   v={t.default_locale} />
              <Meta k="Timezone" v={t.timezone} />
              <Meta k="Owner"    v={t.owner_email_masked} />
              <Meta k="Owner-Name" v={t.owner_full_name} />
              <Meta k="Erstellt" v={t.created_at?.slice(0, 10)} />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="metrics" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi k="Nutzer" v={t.users_count} />
            <Kpi k="Kampagnen (aktiv)" v={t.active_campaigns_count} />
            <Kpi k="Budget-Frames (aktiv)" v={t.active_budget_frames} />
            <Kpi k="Integrationen (verbunden)" v={t.active_integrations} />
          </div>
        </TabsContent>
        <TabsContent value="audit">
          <Card>
            <CardContent className="pt-4">
              {(t.recent_audit_events || []).length === 0 ? (
                <div className="text-sm text-muted-foreground">Keine Audit-Ereignisse.</div>
              ) : (
                <ul className="text-xs space-y-1">
                  {t.recent_audit_events.map((e, i) => (
                    <li key={i} className="border-b border-border/60 py-1"
                        data-testid={`tenant-audit-row-${i}`}>
                      <code>{e.occurred_at?.slice(0, 19)?.replace("T", " ")}</code>
                      {" · "}<b>{e.action}</b>
                      {" · "}{e.resource_type}
                      {e.actor?.id && <> · <span className="text-muted-foreground">{e.actor.id}</span></>}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="gdpr" className="space-y-4">
          <GdprPanel tenantId={id} tenantSlug={t.slug}
                       tenantStatus={t.status}
                       onStatusChange={() => load()} />
        </TabsContent>
      </Tabs>

      {showMetadata && (
        <MetadataSnapshotModal tenantId={id}
                                  onClose={() => setShowMetadata(false)} />
      )}
    </div>
  );
}

function Meta({ k, v }) {
  return (
    <>
      <div className="text-muted-foreground">{k}</div>
      <div>{v || "—"}</div>
    </>
  );
}

function Kpi({ k, v }) {
  return (
    <div className="rounded-md border bg-card px-3 py-3">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</div>
      <div className="text-2xl font-semibold mt-1">{v ?? 0}</div>
    </div>
  );
}

function ActionDialog({ testIdRoot, trigger, title, body, onConfirm,
                             confirmDisabled, confirmLabel }) {
  return (
    <Dialog>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent data-testid={`tenant-action-dialog-${testIdRoot}`}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        {body}
        <DialogFooter>
          <Button
            size="sm"
            onClick={onConfirm}
            disabled={confirmDisabled}
            data-testid={`tenant-action-confirm-${testIdRoot}`}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
